import React, { useState, useEffect } from 'react';
import { Users, Play, RotateCcw, Crown, Eye, EyeOff, Timer } from 'lucide-react';
import { db } from './firebase';
import { doc, onSnapshot, setDoc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';

const GAME_ID = 'current-game';

// Structure initiale de la base de données
const createInitialGameStructure = () => ({
  sessionOpen: false,
  players: [],
  onlinePlayers: [],
  gameSettings: {
    commonWord: '',
    secretWord: ''
  },
  playersWithSecret: [],
  gamePhase: 'login',
  wordDisplayTimer: 0,
  createdAt: serverTimestamp(),
  lastUpdated: serverTimestamp()
});

const SecretWordGame = () => {
  // Données LOCALES (ne changent jamais via Firebase)
  const [localPlayer, setLocalPlayer] = useState({
    name: null,
    isAdmin: false,
    isConnected: false
  });

  // Données PARTAGÉES (synchronisées avec Firebase)
  const [gameData, setGameData] = useState({
    sessionOpen: false,
    players: [],
    commonWord: '',
    secretWord: '',
    playersWithSecret: [],
    gamePhase: 'login',
    wordDisplayTimer: 0,
    onlinePlayers: []
  });

  // État des mots admin - PERSISTANT et indépendant
  const [adminWords, setAdminWords] = useState({
    commonWord: '',
    secretWord: ''
  });

  const [loading, setLoading] = useState(true);

  // Heartbeat pour maintenir la connexion
  useEffect(() => {
    if (!localPlayer.isConnected || !localPlayer.name) return;

    const updateHeartbeat = async () => {
      const gameRef = doc(db, 'games', GAME_ID);
      try {
        const gameSnap = await getDoc(gameRef);
        if (gameSnap.exists()) {
          const currentData = gameSnap.data();
          const currentOnline = currentData.onlinePlayers || [];
          
          const updatedOnline = currentOnline.filter(p => p.name !== localPlayer.name);
          updatedOnline.push({
            name: localPlayer.name,
            lastSeen: new Date().toISOString(),
            isAdmin: localPlayer.isAdmin
          });

          await updateDoc(gameRef, {
            onlinePlayers: updatedOnline,
            lastUpdated: serverTimestamp()
          });
        }
      } catch (error) {
        console.error("❌ Erreur heartbeat:", error);
      }
    };

    updateHeartbeat();
    const interval = setInterval(updateHeartbeat, 10000);
    return () => clearInterval(interval);
  }, [localPlayer.isConnected, localPlayer.name, localPlayer.isAdmin]);

  // Initialiser la base de données et écouter les changements
  useEffect(() => {
    const initializeGame = async () => {
      const gameRef = doc(db, 'games', GAME_ID);
      
      try {
        const gameSnap = await getDoc(gameRef);
        
        if (!gameSnap.exists()) {
          console.log("🔄 Initialisation de la structure du jeu...");
          await setDoc(gameRef, createInitialGameStructure());
          console.log("✅ Structure du jeu créée avec succès !");
        } else {
          console.log("✅ Structure du jeu trouvée, connexion en cours...");
        }
      } catch (error) {
        console.error("❌ Erreur lors de l'initialisation:", error);
      }
    };

    initializeGame().then(() => {
      const gameRef = doc(db, 'games', GAME_ID);
      
      const unsubscribe = onSnapshot(gameRef, (doc) => {
        if (doc.exists()) {
          const firebaseData = doc.data();
          
          setGameData({
            sessionOpen: firebaseData.sessionOpen || false,
            players: firebaseData.players || [],
            commonWord: firebaseData.gameSettings?.commonWord || '',
            secretWord: firebaseData.gameSettings?.secretWord || '',
            playersWithSecret: firebaseData.playersWithSecret || [],
            gamePhase: firebaseData.gamePhase || 'login',
            wordDisplayTimer: firebaseData.wordDisplayTimer || 0,
            onlinePlayers: firebaseData.onlinePlayers || []
          });
          
          // Log seulement pour les changements importants
          if (firebaseData.gamePhase === 'wordDisplay' || firebaseData.gamePhase === 'playing') {
            console.log("🔄 Phase de jeu:", firebaseData.gamePhase);
          }
        }
        setLoading(false);
      }, (error) => {
        console.error("❌ Erreur Firebase:", error);
        setLoading(false);
      });

      return () => unsubscribe();
    });
  }, []);

  // Timer pour l'affichage des mots
  useEffect(() => {
    let interval = null;
    if (gameData.gamePhase === 'wordDisplay' && gameData.wordDisplayTimer > 0) {
      interval = setInterval(async () => {
        const newTimer = gameData.wordDisplayTimer - 1;
        const gameRef = doc(db, 'games', GAME_ID);
        
        try {
          await updateDoc(gameRef, {
            wordDisplayTimer: newTimer,
            gamePhase: newTimer === 0 ? 'playing' : 'wordDisplay',
            lastUpdated: serverTimestamp()
          });
          
          if (newTimer === 0) {
            console.log("⏰ Timer terminé, passage en mode jeu");
          }
        } catch (error) {
          console.error("❌ Erreur lors de la mise à jour du timer:", error);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameData.gamePhase, gameData.wordDisplayTimer]);

  // Nettoyer les joueurs offline
  useEffect(() => {
    const cleanupOfflinePlayers = async () => {
      if (gameData.onlinePlayers.length === 0) return;
      
      const now = new Date();
      const thirtySecondsAgo = new Date(now - 30000);
      
      const stillOnline = gameData.onlinePlayers.filter(player => {
        const lastSeen = new Date(player.lastSeen);
        return lastSeen > thirtySecondsAgo;
      });
      
      if (stillOnline.length !== gameData.onlinePlayers.length) {
        const gameRef = doc(db, 'games', GAME_ID);
        await updateDoc(gameRef, {
          onlinePlayers: stillOnline,
          lastUpdated: serverTimestamp()
        });
        console.log(`🧹 Nettoyage: ${gameData.onlinePlayers.length - stillOnline.length} joueurs déconnectés`);
      }
    };

    const interval = setInterval(cleanupOfflinePlayers, 15000);
    return () => clearInterval(interval);
  }, [gameData.onlinePlayers]);

  // Mettre à jour Firebase
  const updateFirebaseGame = async (updates) => {
    const gameRef = doc(db, 'games', GAME_ID);
    try {
      await updateDoc(gameRef, {
        ...updates,
        lastUpdated: serverTimestamp()
      });
      console.log("✅ Mise à jour Firebase réussie:", updates);
    } catch (error) {
      console.error("❌ Erreur lors de la mise à jour Firebase:", error);
      if (error.code === 'not-found') {
        await setDoc(gameRef, {
          ...createInitialGameStructure(),
          ...updates,
          lastUpdated: serverTimestamp()
        });
        console.log("✅ Document créé avec succès");
      }
    }
  };

  const handleLogin = async (playerName) => {
    if (playerName === "AdminDuJeu") {
      setLocalPlayer({
        name: playerName,
        isAdmin: true,
        isConnected: true
      });
      console.log("👑 Admin connecté localement");
    } else {
      if (!gameData.sessionOpen) {
        alert("Aucune session n'est ouverte. Attendez que l'admin ouvre une session.");
        return;
      }
      
      const gameRef = doc(db, 'games', GAME_ID);
      try {
        const gameSnap = await getDoc(gameRef);
        if (!gameSnap.exists()) {
          alert("Erreur: Aucune session trouvée.");
          return;
        }
        
        const currentData = gameSnap.data();
        const currentPlayers = currentData.players || [];
        
        if (currentPlayers.find(p => p.name === playerName)) {
          alert("Ce nom est déjà pris !");
          return;
        }

        const newPlayer = { 
          name: playerName, 
          word: '', 
          hasSecret: false,
          joinedAt: new Date().toISOString()
        };
        
        const updatedPlayers = [...currentPlayers, newPlayer];
        await updateFirebaseGame({ players: updatedPlayers });
        
        setLocalPlayer({
          name: playerName,
          isAdmin: false,
          isConnected: true
        });
        
        console.log(`🎮 Joueur ${playerName} connecté (${updatedPlayers.length} joueurs total)`);
        
      } catch (error) {
        console.error("❌ Erreur lors de la connexion:", error);
        alert("Erreur lors de la connexion. Veuillez réessayer.");
      }
    }
  };

  const openSession = async () => {
    await updateFirebaseGame({ sessionOpen: true });
    console.log("🚀 Session ouverte");
  };

  const assignWordsToPlayer = async (playerName, hasSecret) => {
    const updatedPlayersWithSecret = hasSecret 
      ? [...gameData.playersWithSecret, playerName]
      : gameData.playersWithSecret.filter(name => name !== playerName);
    
    await updateFirebaseGame({ playersWithSecret: updatedPlayersWithSecret });
    console.log(`🎯 ${playerName} ${hasSecret ? 'a le mot secret' : 'a le mot commun'}`);
  };

  const startGame = async () => {
    if (!gameData.commonWord || !gameData.secretWord) {
      alert("Veuillez définir les deux mots !");
      return;
    }

    if (gameData.playersWithSecret.length === 0) {
      alert("Veuillez sélectionner au moins un joueur pour le mot secret !");
      return;
    }

    const updatedPlayers = gameData.players.map(player => ({
      ...player,
      word: gameData.playersWithSecret.includes(player.name) ? gameData.secretWord : gameData.commonWord,
      hasSecret: gameData.playersWithSecret.includes(player.name)
    }));

    await updateFirebaseGame({
      players: updatedPlayers,
      gamePhase: 'wordDisplay',
      wordDisplayTimer: 10
    });
    console.log("🎲 Jeu lancé !");
  };

  const newGame = async () => {
    // Reset du jeu SANS déconnecter les joueurs
    const resetPlayers = gameData.players.map(player => ({
      ...player,
      word: '',
      hasSecret: false
    }));

    await updateFirebaseGame({
      players: resetPlayers, // Garde les joueurs mais reset leurs mots
      playersWithSecret: [], // Reset les assignations
      gamePhase: 'lobby', // Retour au lobby
      wordDisplayTimer: 0
      // On ne touche PAS aux mots ni aux joueurs en ligne
    });
    console.log("🔄 Nouvelle partie créée - Joueurs conservés");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 text-center border border-white/20 shadow-2xl">
          <div className="animate-spin w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-white text-xl mb-2">🔥 Initialisation Firebase...</div>
          <div className="text-white/70 text-sm">Vérification de la structure de données</div>
        </div>
      </div>
    );
  }

  const LoginPage = () => {
    const [playerName, setPlayerName] = useState('');

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 w-full max-w-md border border-white/20 shadow-2xl">
          <div className="text-center mb-8">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Eye className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Le Loup-Garou du Mot</h1>
            <p className="text-white/70">Entrez votre nom pour rejoindre la partie</p>
            <div className="text-green-400 text-sm mt-2">🔥 Connecté à Firebase</div>
            {gameData.onlinePlayers.length > 0 && (
              <div className="text-blue-300 text-xs mt-1">
                {gameData.onlinePlayers.length} joueur{gameData.onlinePlayers.length > 1 ? 's' : ''} en ligne
              </div>
            )}
          </div>

          <div>
            <input
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && playerName.trim()) {
                  handleLogin(playerName.trim());
                }
              }}
              placeholder="Votre nom..."
              className="w-full p-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-400 text-lg"
            />
            <button
              onClick={() => {
                if (playerName.trim()) handleLogin(playerName.trim());
              }}
              className="w-full mt-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-white p-4 rounded-xl font-semibold text-lg hover:from-yellow-500 hover:to-orange-600 transition-all duration-200 transform hover:scale-105"
            >
              Rejoindre la partie
            </button>
          </div>

          {!gameData.sessionOpen && !localPlayer.isAdmin && (
            <div className="mt-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl">
              <p className="text-red-200 text-center text-sm">
                ⏳ En attente qu'un admin ouvre une session...
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const AdminInterface = () => {
    const handleWordChange = (field, value) => {
      setAdminWords(prev => ({ ...prev, [field]: value }));
    };

    const applyWords = async () => {
      if (!adminWords.commonWord.trim() || !adminWords.secretWord.trim()) {
        alert("Veuillez remplir les deux mots !");
        return;
      }

      await updateFirebaseGame({
        gameSettings: {
          commonWord: adminWords.commonWord.trim(),
          secretWord: adminWords.secretWord.trim()
        }
      });
      console.log("🎯 Mots appliqués:", adminWords);
    };

    const resetWords = () => {
      setAdminWords({ commonWord: '', secretWord: '' });
    };

    const loadCurrentWords = () => {
      setAdminWords({
        commonWord: gameData.commonWord || '',
        secretWord: gameData.secretWord || ''
      });
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-2xl mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Crown className="w-8 h-8 text-yellow-400" />
                <div>
                  <h1 className="text-3xl font-bold text-white">Interface Admin</h1>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="text-green-400 text-sm">🔥 Firebase connecté</div>
                    <div className="text-white/50 text-xs">
                      • {gameData.players.length} joueurs inscrits
                      • {gameData.onlinePlayers.length} en ligne
                      • Phase: {gameData.gamePhase}
                    </div>
                  </div>
                </div>
              </div>
              {!gameData.sessionOpen && (
                <button
                  onClick={openSession}
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                >
                  Ouvrir la session
                </button>
              )}
            </div>

            {gameData.sessionOpen && (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/5 rounded-xl p-4">
                  <h3 className="text-xl font-semibold text-white mb-4">Configuration des mots</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white/70 mb-2">Mot commun</label>
                      <input
                        type="text"
                        value={adminWords.commonWord}
                        onChange={(e) => handleWordChange('commonWord', e.target.value)}
                        placeholder="Ex: Pizza"
                        className="w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 mb-2">Mot secret (imposteur)</label>
                      <input
                        type="text"
                        value={adminWords.secretWord}
                        onChange={(e) => handleWordChange('secretWord', e.target.value)}
                        placeholder="Ex: Lasagnes"
                        className="w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={applyWords}
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-lg font-semibold transition-colors"
                      >
                        ✅ Appliquer les mots
                      </button>
                      <button
                        onClick={loadCurrentWords}
                        className="bg-green-500 hover:bg-green-600 text-white p-3 rounded-lg font-semibold transition-colors"
                        title="Charger les mots actuels depuis Firebase"
                      >
                        📥
                      </button>
                      <button
                        onClick={resetWords}
                        className="bg-gray-500 hover:bg-gray-600 text-white p-3 rounded-lg font-semibold transition-colors"
                        title="Vider les champs"
                      >
                        🗑️
                      </button>
                    </div>
                    {(gameData.commonWord || gameData.secretWord) && (
                      <div className="bg-white/5 rounded-lg p-3">
                        <div className="text-center text-sm text-white/70 mb-1">
                          ⚡ Mots actifs dans Firebase:
                        </div>
                        <div className="text-center text-sm">
                          <span className="text-green-400">"{gameData.commonWord}"</span>
                          <span className="text-white/50"> vs </span>
                          <span className="text-red-400">"{gameData.secretWord}"</span>
                        </div>
                      </div>
                    )}
                    {(!gameData.commonWord && !gameData.secretWord) && (
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                        <div className="text-center text-sm text-yellow-300">
                          ⚠️ Aucun mot configuré dans Firebase
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-4">
                  <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Joueurs inscrits ({gameData.players.length})
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {gameData.players.map((player, index) => {
                      const isOnline = gameData.onlinePlayers.some(p => p.name === player.name);
                      return (
                        <div key={index} className="flex items-center justify-between bg-white/5 p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium">{player.name}</span>
                            <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-400' : 'bg-gray-500'}`} title={isOnline ? 'En ligne' : 'Hors ligne'}></div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-white/70">
                              {gameData.playersWithSecret.includes(player.name) ? '🔴 Secret' : '🟢 Commun'}
                            </span>
                            <button
                              onClick={() => assignWordsToPlayer(player.name, !gameData.playersWithSecret.includes(player.name))}
                              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                gameData.playersWithSecret.includes(player.name)
                                  ? 'bg-red-500 hover:bg-red-600 text-white'
                                  : 'bg-gray-500 hover:bg-gray-600 text-white'
                              }`}
                            >
                              {gameData.playersWithSecret.includes(player.name) ? 'Secret' : 'Commun'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {gameData.sessionOpen && gameData.players.length > 0 && (
              <div className="mt-6 flex gap-4">
                <button
                  onClick={startGame}
                  disabled={gameData.gamePhase === 'wordDisplay'}
                  className="flex items-center gap-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-500 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                >
                  <Play className="w-5 h-5" />
                  Lancer le jeu
                </button>
                
                <button
                  onClick={newGame}
                  className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                >
                  <RotateCcw className="w-5 h-5" />
                  Nouvelle partie
                </button>

                <button
                  onClick={async () => {
                    if (window.confirm("Êtes-vous sûr de vouloir tout effacer ? Tous les joueurs seront déconnectés.")) {
                      await updateFirebaseGame({
                        players: [],
                        gameSettings: { commonWord: '', secretWord: '' },
                        playersWithSecret: [],
                        gamePhase: 'lobby',
                        wordDisplayTimer: 0,
                        onlinePlayers: []
                      });
                      resetWords();
                      console.log("🗑️ Tout effacé");
                    }
                  }}
                  className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                >
                  🗑️ Tout effacer
                </button>
              </div>
            )}
          </div>

          {gameData.gamePhase === 'playing' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-2xl">
              <h2 className="text-2xl font-bold text-white mb-4">Partie en cours</h2>
              <div className="grid gap-4">
                <div className="bg-white/5 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-white mb-3">Répartition des mots</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-white/70 mb-2">Mot commun: <span className="text-green-400 font-semibold">{gameData.commonWord}</span></h4>
                      <div className="space-y-1">
                        {gameData.players.filter(p => !p.hasSecret).map(player => (
                          <div key={player.name} className="text-green-300 text-sm">• {player.name}</div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-white/70 mb-2">Mot secret: <span className="text-red-400 font-semibold">{gameData.secretWord}</span></h4>
                      <div className="space-y-1">
                        {gameData.players.filter(p => p.hasSecret).map(player => (
                          <div key={player.name} className="text-red-300 text-sm">• {player.name}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                  <h4 className="text-blue-300 font-semibold mb-2">📊 Informations de partie</h4>
                  <div className="grid md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-blue-200">Total joueurs:</span>
                      <span className="text-white ml-2">{gameData.players.length}</span>
                    </div>
                    <div>
                      <span className="text-blue-200">En ligne:</span>
                      <span className="text-green-300 ml-2">{gameData.onlinePlayers.length}</span>
                    </div>
                    <div>
                      <span className="text-blue-200">Imposteurs:</span>
                      <span className="text-red-300 ml-2">{gameData.playersWithSecret.length}</span>
                    </div>
                    <div>
                      <span className="text-blue-200">Innocents:</span>
                      <span className="text-green-300 ml-2">{gameData.players.length - gameData.playersWithSecret.length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const PlayerInterface = () => {
    const currentPlayerData = gameData.players.find(p => p.name === localPlayer.name);

    if (!currentPlayerData && localPlayer.isConnected && !localPlayer.isAdmin) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 w-full max-w-md border border-white/20 shadow-2xl text-center">
            <div className="animate-spin w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-white mb-4">Connexion en cours...</h2>
            <p className="text-white/70">Ajout à la partie en cours</p>
          </div>
        </div>
      );
    }

    if (gameData.gamePhase === 'lobby') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 w-full max-w-md border border-white/20 shadow-2xl text-center">
            <Users className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Bienvenue {localPlayer.name}!</h2>
            <p className="text-white/70 mb-4">Vous êtes connecté à la partie.</p>
            <div className="text-green-400 text-sm mb-4">🔥 Synchronisé avec Firebase</div>
            
            <div className="bg-white/5 rounded-xl p-4 mb-4">
              <h3 className="text-white font-semibold mb-2">Joueurs inscrits ({gameData.players.length})</h3>
              <div className="space-y-1">
                {gameData.players.map(player => {
                  const isOnline = gameData.onlinePlayers.some(p => p.name === player.name);
                  return (
                    <div key={player.name} className="flex items-center justify-between">
                      <span className="text-white/70 text-sm">• {player.name}</span>
                      <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-400' : 'bg-gray-500'}`}></div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 mb-4">
              <div className="text-blue-300 text-sm">
                {gameData.onlinePlayers.length} joueur{gameData.onlinePlayers.length > 1 ? 's' : ''} en ligne
              </div>
            </div>
            
            <p className="text-yellow-300 mt-4 text-sm">⏳ En attente que l'admin lance la partie...</p>
          </div>
        </div>
      );
    }

    if (gameData.gamePhase === 'wordDisplay' && currentPlayerData) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 w-full max-w-md border border-white/20 shadow-2xl text-center">
            <div className="mb-6">
              <Timer className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
              <div className="text-6xl font-bold text-yellow-400 mb-2">{gameData.wordDisplayTimer}</div>
              <p className="text-white/70">secondes restantes</p>
            </div>
            
            <div className="bg-white/10 rounded-2xl p-6 mb-6">
              <h3 className="text-white/70 mb-2">Votre mot est :</h3>
              <div className="text-4xl font-bold mb-2 text-yellow-400">
                {currentPlayerData.word}
              </div>
            </div>
            
            <p className="text-white/50 text-sm">Mémorisez bien votre mot, il va disparaître !</p>
          </div>
        </div>
      );
    }

    if (gameData.gamePhase === 'playing') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 w-full max-w-md border border-white/20 shadow-2xl text-center">
            <EyeOff className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Partie en cours</h2>
            <p className="text-white/70 mb-6">
              Le jeu a commencé ! Donnez des indices subtils liés à votre mot sans le révéler.
            </p>
            
            <div className="bg-white/5 rounded-xl p-4 mb-6">
              <h3 className="text-white font-semibold mb-2">Rappel des règles :</h3>
              <ul className="text-white/70 text-sm space-y-1 text-left">
                <li>• Donnez un indice lié à votre mot</li>
                <li>• Ne révélez pas directement le mot</li>
                <li>• Observez les autres joueurs</li>
                <li>• Trouvez les imposteurs !</li>
              </ul>
            </div>

            <div className="bg-white/5 rounded-xl p-4 mb-4">
              <h4 className="text-white font-semibold mb-2">Joueurs dans la partie:</h4>
              <div className="grid grid-cols-2 gap-1">
                {gameData.players.map(player => {
                  const isOnline = gameData.onlinePlayers.some(p => p.name === player.name);
                  return (
                    <div key={player.name} className="flex items-center gap-1">
                      <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-400' : 'bg-gray-500'}`}></div>
                      <span className="text-white/70 text-xs">{player.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-4">
              <p className="text-yellow-200 text-sm">
                💡 Votre mot n'est plus affiché. Souvenez-vous de ce qu'il était !
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 w-full max-w-md border border-white/20 shadow-2xl text-center">
          <p className="text-white">En attente...</p>
        </div>
      </div>
    );
  };

  // Rendu principal
  if (!localPlayer.isConnected) {
    return <LoginPage />;
  }

  if (localPlayer.isAdmin) {
    return <AdminInterface />;
  }

  return <PlayerInterface />;
};

export default SecretWordGame;